import React, { useState, useCallback, useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Stack, CircularProgress } from '@mui/material'
import { startOfMonth, endOfMonth } from 'date-fns'

import { useSites } from 'store'

import WidgetGlobalChromecast from './WidgetGlobalChromecast/WidgetGlobalChromecast'
import WidgetDailyChromecastUsage from './WidgetDailyChromecastUsage/WidgetDailyChromecastUsage'
import Filters from '../components/Filters'
import EmptyScreen from 'packages/ui/EmptyScreen'
import WidgetAppUsage from './WidgetAppUsage/WidgetAppUsage'

const FIND_CHROMECAST_STATS = gql`
  query StatsChromecasts($sfId: ID!, $from: String!, $to: String!) {
    stats: statsChromecasts(site_sf_id: $sfId, from: $from, to: $to) {
      use_distinct_count
      installation_count
      app_use {
        name
        count
      }
      day_use {
        date
        value
      }
    }
  }
`

interface ChromecastStatsData {
  use_distinct_count: number
  installation_count: number
  day_use: { date: string; value: number }[]
  app_use: { name: string; count: number }[]
}

interface ChromecastData {
  stats: ChromecastStatsData
}

const Chromecasts = () => {
  const { t } = useTranslation()
  const [{ selected }] = useSites()
  const wifirstsiteId = useMemo(() => (selected && selected.length === 1 ? selected[0].sf_id : null), [selected])

  const now = new Date()
  const start = startOfMonth(now)
  const end = endOfMonth(now)
  
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ 
    start, 
    end 
  })
  
  const { data, loading, error } = useQuery<ChromecastData>(FIND_CHROMECAST_STATS, {
    variables: {
      sfId: wifirstsiteId,
      from: dateRange.start ? Math.floor(startOfMonth(dateRange.start).getTime() / 1000).toString() : '0',
      to: dateRange.end ? Math.floor(endOfMonth(dateRange.end).getTime() / 1000).toString() : '0'
    },
    skip: !wifirstsiteId || !dateRange.start || !dateRange.end
  })

  const appUseData = useMemo(() => {
    return data?.stats?.app_use || []
  }, [data])
  
  const dailyUsageData = useMemo(() => {
    if (!data?.stats?.day_use || !dateRange.start || !dateRange.end) return [];
    
    const selectedMonth = dateRange.start.getMonth();
    const selectedYear = dateRange.start.getFullYear();
  
    return data.stats.day_use
      .filter(({ date }) => {
        const dataDate = new Date(date);
        return dataDate.getMonth() === selectedMonth && 
               dataDate.getFullYear() === selectedYear;
      })
      .map(({ date, value }) => ({
        timestamp: new Date(date).getTime(),
        value
      }));
  }, [data, dateRange]);
  

  const handleDateChange = useCallback((start, end) => {
    setDateRange({ start, end })
  }, [setDateRange])

  const renderContent = () => {
    if (!selected || selected.length === 0) {
      return <EmptyScreen icon="square-poll-vertical">{t('statistics.chromecasts.select_site')}</EmptyScreen>
    }
    if (selected.length > 1) {
      return <EmptyScreen icon="square-poll-vertical">{t('statistics.chromecasts.select_single_site')}</EmptyScreen>
    }
    if (loading) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
          <CircularProgress />
        </Stack>
      )
    }
    if (error) {
      return <p>Error: {error.message}</p>
    }
    if (!data?.stats || (!data.stats.use_distinct_count && !data.stats.installation_count && !data.stats.day_use?.length && !data.stats.app_use?.length)) {
      return <EmptyScreen icon="square-poll-vertical">{t('statistics.chromecasts.no_data_filter')}</EmptyScreen>
    }
    return (
      <>
        <Stack direction="row" spacing={1.5} sx={{ maxHeight: '16rem', flex: 1 }}>
          <WidgetGlobalChromecast
            useDistinctCount={data?.stats?.use_distinct_count}
            installationCount={data?.stats?.installation_count}
          />
          <WidgetAppUsage appUseData={appUseData} />
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ flex: 1 }}>
          {dateRange.start && dateRange.end && (
            <WidgetDailyChromecastUsage data={dailyUsageData} startDate={dateRange.start} endDate={dateRange.end} />
          )}
        </Stack>
      </>
    )
  }

  return (
    <Stack gap={2} flex={1}>
      <Filters
        section={'chromecasts'}
        onDateChange={handleDateChange}
        startDate={dateRange.start}
        endDate={dateRange.end}
      />
      {renderContent()}
    </Stack>
  )
}

export default Chromecasts
